import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

// Api
import ObjectTuningApi from "../../../../api/object-tuning/ObjectTuningApi";

// Components
import ConditionalRender from "../../../../helpers/ConditionalRender";
import ColumnsTable from "./ColumnsTable";
import IndexesTable from "./IndexesTable";
import InsightsTable from "./InsightsTable";
import ObjectPanelTable from "./ObjectPanelTable";
import ObjectWidgets from "./ObjectWidgets";
import ObjectTabs from "./ObjectTabs";

// Types
import ObjectAnalysisType from "../../../../types/ObjectBrowser/ObjectAnalysisType";
import TableStatisticsType from "../../../../types/ObjectBrowser/TableStatisticsType";
import IndexStatisticsType from "../../../../types/ObjectBrowser/IndexStatisticsType";
import ObjectDetailsType from "../../../../types/ObjectBrowser/ObjectDetailsType";
import { ObjectDetailsPanelProps, TABS } from "../../../../types/ObjectBrowser/ComponentTypes";
import InstanceTarget from "../../../../types/instance/InstanceTarget";

// Context
import { TimeRangeContext } from "../../../../context/TimeRangeContext";
import { InstanceContext } from "../../../../context/InstanceContext";

// Hooks
import { useTabManagement } from "./hooks/useTabManagement";

// Styles
import "./style.scss";

const ObjectDetailsPanel: React.FC<ObjectDetailsPanelProps> = ({ open, object, onClose }) => {
    const { activeTab, handleTabChange } = useTabManagement(TABS);
    const [objectAnalysis, setObjectAnalysis] = useState<ObjectAnalysisType[]>([]);
    const [selectedObject, setSelectedObject] = useState<ObjectDetailsType | null>(object);
    const [loading, setLoading] = useState(true);
    const [optionsList, setOptionsLIst] = useState<TableStatisticsType[] | IndexStatisticsType[]>([]);

    const timeRangeContext = useContext(TimeRangeContext);
    const { instances } = useContext(InstanceContext);
    const { instanceId } = useParams();

    const currentInstance = instances.find(instance => instance.id === Number(instanceId)) as InstanceTarget;
    useEffect(() => {
        setSelectedObject(object);
    }, [object]);

    useEffect(() => {
        const fetchData = async () => {
            if (!currentInstance || !selectedObject) return;

            setLoading(true);
            try {
                if (selectedObject.tableschema && selectedObject.tableschema !== 'Unknown') {
                    const objectAnalysisData = await ObjectTuningApi.getObjectAnalysis(
                            timeRangeContext,
                            currentInstance.id,
                            selectedObject
                    );

                    setObjectAnalysis(objectAnalysisData);
                } else {
                    if (selectedObject.objectType === "Index") {
                        const indexStatisticData = await ObjectTuningApi.getIndexStatistic(timeRangeContext, currentInstance.id);
                        const filteredData = indexStatisticData.filter(item => item.indexname === selectedObject.indexname);
                        if (filteredData.length === 1) {
                            setSelectedObject((prev: ObjectDetailsType | null) => prev ? {
                                ...prev,
                                indexschema: filteredData[0].indexschema,
                                indexname: filteredData[0].indexname,
                                indexsizebytes: filteredData[0].indexsizebytes,
                                tablesizebytes: filteredData[0].tablesizebytes,
                                tableschema: filteredData[0].tableschema,
                                tablename: filteredData[0].tablename,
                                rows: null
                            } : null);
                        } else {
                            const modifiedData = filteredData.map(item => ({
                                ...item,
                                type: 'Index',
                            }));
                            setOptionsLIst(modifiedData);
                        }
                    } else {
                        const tableStatisticsData = await ObjectTuningApi.getTableStatistic(timeRangeContext, currentInstance.id);
                        const filteredData = tableStatisticsData.filter(item => item.tablename === selectedObject.tablename);
                        if (filteredData.length === 1) {
                            setSelectedObject((prev: ObjectDetailsType | null) => prev ? {
                                ...prev,
                                tableschema: filteredData[0].tableschema,
                                tablesizebytes: filteredData[0].tablesizebytes,
                                indexsizebytes: filteredData[0].indexsizebytes,
                                tablename: filteredData[0].tablename,
                                rows: filteredData[0].rows
                            } : null);
                        } else {
                            const modifiedData = filteredData.map(item => ({
                            ...item,
                            type: 'Table',
                        }));
                            setOptionsLIst(modifiedData);
                        }
                    }
                }
            } catch (error) {
                console.error('Failed to fetch object data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [currentInstance, timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval, selectedObject]);

    if (!selectedObject) return null;

    return (
            <div
                    aria-hidden={!open}
                    tabIndex={-1}
                    role="dialog"
                    className={`object-browser-details-panel ${open ? '' : 'hidden'}`}
            >
                <div className="panel-header">
                <span className='header-filters'>
                    <i
                            className={selectedObject?.objectType === "Table" ? "fa fa-table" : "fa fa-list-ol"}
                            style={{ marginRight: 6 }}
                            aria-hidden="true"
                    />
                    <span>
                        {selectedObject?.objectType === "Table" ? selectedObject?.tablename : selectedObject?.indexname}
                        <span style={{ fontWeight: 400, fontSize: 14, color: "#666", marginLeft: 4 }}>
                            ({selectedObject?.objectType.toLowerCase()})
                        </span>
                    </span>

                        <ConditionalRender if={selectedObject?.tableschema}>
                            <span>
                                {selectedObject?.tableschema ?? "Unknown"}
                                <span style={{ fontWeight: 400, fontSize: 14, color: "#666", marginLeft: 4 }}>
                                    (schema)
                                </span>
                            </span>
                        </ConditionalRender>

                        <ConditionalRender if={selectedObject?.database}>
                            <span>
                                {selectedObject?.database ?? "Unknown"}
                                <span style={{ fontWeight: 400, fontSize: 14, color: "#666", marginLeft: 4 }}>
                                    (database)
                                </span>
                            </span>
                        </ConditionalRender>
                        <ConditionalRender if={selectedObject?.tablename && selectedObject?.objectType === "Index"}>
                            <span>
                                {selectedObject?.tablename ?? "Unknown"}
                                <span style={{ fontWeight: 400, fontSize: 14, color: "#666", marginLeft: 4 }}>
                                    (table)
                                </span>
                            </span>
                        </ConditionalRender>
                </span>
                    <button className="close-button" onClick={onClose} aria-label="Close panel">
                        &times;
                    </button>
                </div>

                <div className="details-content">
                    <ConditionalRender if={selectedObject.tableschema && selectedObject.tableschema !== 'Unknown' && !loading}>
                        <ObjectWidgets
                                selectedObject={selectedObject}
                                objectAnalysis={objectAnalysis}
                        />

                        <ObjectTabs
                                tabs={TABS}
                                activeTab={activeTab}
                                onTabChange={handleTabChange}
                        />

                        <ConditionalRender if={activeTab === "definition"}>
                            <ColumnsTable selectedItem={selectedObject}/>
                            <IndexesTable setSelectedObject={setSelectedObject} selectedItem={selectedObject}/>
                        </ConditionalRender>

                        <ConditionalRender if={activeTab === "insights"}>
                            <InsightsTable objectAnalysis={objectAnalysis} selectedItem={selectedObject}/>
                        </ConditionalRender>
                    </ConditionalRender>

                    <ConditionalRender if={(!selectedObject.tableschema || selectedObject.tableschema === 'Unknown') && !loading}>
                        <ObjectPanelTable
                                loading={loading}
                                optionsList={optionsList}
                                instance={currentInstance}
                                selectedItem={selectedObject}
                                setSelectedObject={setSelectedObject}
                        />
                    </ConditionalRender>

                    <ConditionalRender if={loading}>
                        <div className="w-100 text-center text-muted my-3 statement-chart">
                            <div className="loader spinner">
                            </div>
                            <p className="mt-3">
                                Loading data...
                            </p>
                        </div>
                    </ConditionalRender>
                </div>
            </div>
    );
};

export default ObjectDetailsPanel;
